import { toCanonicalCitations } from "@/components/MedicalChronology/Visits/utils/visitCitations";
import type { MedVisit } from "@/types/medchron";

import type { MatchVisitsResult, MatchVisitsWithModeResult } from "./types";
import { CompareVersionsConstants, VisitMatchMode } from "./types";

export const matchVisitsById = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsResult => {
  const oldById = new Map(oldVisits.map((v) => [v.visit_id, v]));
  const newById = new Map(newVisits.map((v) => [v.visit_id, v]));

  const removed = oldVisits.filter((v) => !newById.has(v.visit_id));
  const added = newVisits.filter((v) => !oldById.has(v.visit_id));
  const matched: Array<[MedVisit, MedVisit]> = [];
  for (const oldVisit of oldVisits) {
    const newVisit = newById.get(oldVisit.visit_id);
    if (newVisit) matched.push([oldVisit, newVisit]);
  }

  return { removed, added, matched };
};

const buildVisitContentKey = (visit: MedVisit): string =>
  [
    visit.visit_date ?? "",
    visit.visit_type_id,
    ...toCanonicalCitations(visit).map(
      (citation) => `${citation.file_id}:${citation.start_page}-${citation.end_page}`
    ),
  ].join("|");

const tokenizeVisitDetails = (visit: MedVisit): Set<string> =>
  new Set(
    visit.med_visit_details
      .flatMap((detail) => detail.visit_details.toLowerCase().split(/[^a-z0-9]+/))
      .filter((token) => token !== "")
  );

const computeTokenSimilarity = (oldTokens: Set<string>, newTokens: Set<string>): number => {
  if (oldTokens.size === 0 && newTokens.size === 0) return 1;
  let sharedCount = 0;
  oldTokens.forEach((token) => {
    if (newTokens.has(token)) sharedCount += 1;
  });
  return (2 * sharedCount) / (oldTokens.size + newTokens.size);
};

const normalizeName = (name: string | null | undefined): string =>
  (name ?? "").trim().toLowerCase();

const countMatchingProviderFields = (oldVisit: MedVisit, newVisit: MedVisit): number =>
  Number(normalizeName(oldVisit.provider_name) === normalizeName(newVisit.provider_name)) +
  Number(normalizeName(oldVisit.facility_name) === normalizeName(newVisit.facility_name));

const groupIndicesByContentKey = (visits: MedVisit[]): Map<string, number[]> => {
  const indicesByKey = new Map<string, number[]>();
  visits.forEach((visit, index) => {
    const key = buildVisitContentKey(visit);
    const indices = indicesByKey.get(key);
    if (indices) indices.push(index);
    else indicesByKey.set(key, [index]);
  });
  return indicesByKey;
};

interface ContentPairCandidate {
  oldIndex: number;
  newIndex: number;
  similarity: number;
  matchingProviderFieldCount: number;
}

interface TokenizedVisits {
  visits: MedVisit[];
  tokens: Set<string>[];
}

const tokenizeVisits = (visits: MedVisit[]): TokenizedVisits => ({
  visits,
  tokens: visits.map(tokenizeVisitDetails),
});

/**
 * Scores every old/new pair in one content-key group, best pair first.
 * - Provider and facility only break ties: they vary between pipeline runs.
 */
const rankContentPairCandidates = (
  oldIndices: number[],
  newIndices: number[],
  oldSide: TokenizedVisits,
  newSide: TokenizedVisits
): ContentPairCandidate[] =>
  oldIndices
    .flatMap((oldIndex) =>
      newIndices.map(
        (newIndex): ContentPairCandidate => ({
          oldIndex,
          newIndex,
          similarity: computeTokenSimilarity(oldSide.tokens[oldIndex], newSide.tokens[newIndex]),
          matchingProviderFieldCount: countMatchingProviderFields(
            oldSide.visits[oldIndex],
            newSide.visits[newIndex]
          ),
        })
      )
    )
    .sort(
      (left, right) =>
        right.similarity - left.similarity ||
        right.matchingProviderFieldCount - left.matchingProviderFieldCount ||
        left.oldIndex - right.oldIndex ||
        left.newIndex - right.newIndex
    );

/**
 * Greedily pairs ranked candidates of one key group, returning new index by old index.
 * - Equal-sized groups pair fully; unequal ones drop pairs below CONTENT_MATCH_MIN_SIMILARITY.
 * - Equal counts trust the shared date, type and pages over text, so a rewritten visit still pairs.
 */
const assignContentPairs = (
  oldIndices: number[],
  newIndices: number[],
  rankedCandidates: ContentPairCandidate[]
): Map<number, number> => {
  const minSimilarity =
    oldIndices.length === newIndices.length
      ? 0
      : CompareVersionsConstants.CONTENT_MATCH_MIN_SIMILARITY;
  const newIndexByOldIndex = new Map<number, number>();
  const pairedNewIndices = new Set<number>();
  for (const candidate of rankedCandidates) {
    if (
      candidate.similarity < minSimilarity ||
      newIndexByOldIndex.has(candidate.oldIndex) ||
      pairedNewIndices.has(candidate.newIndex)
    ) {
      continue;
    }
    newIndexByOldIndex.set(candidate.oldIndex, candidate.newIndex);
    pairedNewIndices.add(candidate.newIndex);
  }
  return newIndexByOldIndex;
};

/** Pairs visits sharing date, visit type and cited pages, best detail-text similarity first. */
export const matchVisitsByContent = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsResult => {
  const oldSide = tokenizeVisits(oldVisits);
  const newSide = tokenizeVisits(newVisits);
  const newIndicesByKey = groupIndicesByContentKey(newVisits);
  const newIndexByOldIndex = new Map<number, number>();

  groupIndicesByContentKey(oldVisits).forEach((oldIndices, key) => {
    const newIndices = newIndicesByKey.get(key);
    if (!newIndices) return;
    const rankedCandidates = rankContentPairCandidates(oldIndices, newIndices, oldSide, newSide);
    assignContentPairs(oldIndices, newIndices, rankedCandidates).forEach((newIndex, oldIndex) =>
      newIndexByOldIndex.set(oldIndex, newIndex)
    );
  });

  const pairedNewIndices = new Set(newIndexByOldIndex.values());
  const matched: Array<[MedVisit, MedVisit]> = [];
  const removed: MedVisit[] = [];
  oldVisits.forEach((oldVisit, oldIndex) => {
    const newIndex = newIndexByOldIndex.get(oldIndex);
    if (newIndex === undefined) removed.push(oldVisit);
    else matched.push([oldVisit, newVisits[newIndex]]);
  });
  const added = newVisits.filter((_, newIndex) => !pairedNewIndices.has(newIndex));

  return { removed, added, matched };
};

const selectVisitMatchMode = (
  sharedIdCount: number,
  smallerVersionCount: number
): VisitMatchMode =>
  smallerVersionCount === 0 ||
  sharedIdCount / smallerVersionCount >= CompareVersionsConstants.ID_MATCH_MIN_OVERLAP_RATIO
    ? VisitMatchMode.Id
    : VisitMatchMode.Content;

/**
 * Pairs by visit_id when versions share enough IDs (copied versions), else by content (independent runs).
 * - Content mode still pairs the few shared IDs first, which keeps every entry's visitId unique.
 */
export const matchVisits = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[]
): MatchVisitsWithModeResult => {
  const idMatch = matchVisitsById(oldVisits, newVisits);
  const smallerVersionCount = Math.min(oldVisits.length, newVisits.length);
  if (selectVisitMatchMode(idMatch.matched.length, smallerVersionCount) === VisitMatchMode.Id) {
    return { ...idMatch, matchMode: VisitMatchMode.Id };
  }
  const contentMatch = matchVisitsByContent(idMatch.removed, idMatch.added);
  return {
    removed: contentMatch.removed,
    added: contentMatch.added,
    matched: [...idMatch.matched, ...contentMatch.matched],
    matchMode: VisitMatchMode.Content,
  };
};
